/**
 * TeamRegistrationPanel — "bring your own team" for the person registering a
 * team: create the team, share the invite link, manage the roster, pick the
 * captain, pay the team fee (if that option was chosen) and submit for admin
 * approval. Backed by /v1/events/{id}/team-registrations/*.
 */
import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import { payOrder } from '../../utils/payOrder';
import { copyText } from '../../utils/copyText';

const rupees = (paise) => `₹${((paise || 0) / 100).toLocaleString('en-IN')}`;
const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : (e?.message === 'Payment cancelled' ? 'Payment cancelled' : fallback);
};
const ROLE_SHORT = { 'Wicket-keeper': 'WK', 'All-rounder': 'AR', Bowler: 'BOWL', Batter: 'BAT' };
const STATUS = {
  draft: { label: 'Draft — building the roster', cls: 'bg-gray-100 text-gray-600' },
  submitted: { label: 'Submitted — waiting for admin approval', cls: 'bg-amber-100 text-amber-700' },
  approved: { label: 'Approved — your team is in!', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Not accepted', cls: 'bg-red-100 text-red-600' },
};

export default function TeamRegistrationPanel({ eventId, info, user }) {
  const [data, setData] = useState(null);       // { registered: [...], joined }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [teamName, setTeamName] = useState('');
  const [feeMode, setFeeMode] = useState('team');
  const [startNew, setStartNew] = useState(false);

  const base = `/v1/events/${eventId}/team-registrations`;
  const teamFee = info?.fees?.team || 0;
  const playerFee = info?.fees?.player || 0;

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get(`${base}/mine`);
      setData(res.data);
    } catch (e) {
      toast.error(errMsg(e, 'Failed to load your team'));
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await load();
      return true;
    } catch (e) {
      toast.error(errMsg(e, 'Something went wrong'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const team = (data?.registered || [])[0];
  const showCreate = !team || (team.status === 'rejected' && startNew);

  const createTeam = async () => {
    if (!teamName.trim()) { toast.error('Enter a team name'); return; }
    const okay = await act(() => axiosInstance.post(base, { team_name: teamName.trim(), fee_mode: feeMode }),
      'Team created — now invite your players');
    if (okay) { setTeamName(''); setStartNew(false); }
  };

  const inviteUrl = team?.invite_code
    ? `${window.location.origin}/events/${eventId}/register?invite=${team.invite_code}` : '';

  const copyInvite = async () => {
    if (await copyText(inviteUrl)) toast.success('Invite link copied');
    else toast.error('Copy failed — select the link and copy it');
  };

  const payTeamFee = () => act(async () => {
    const order = (await axiosInstance.post(`${base}/${team.id}/payment-order`)).data;
    const paid = await payOrder({
      order,
      description: `${info?.event_title || 'Tournament'} — team fee (${team.team_name})`,
      prefill: { contact: user?.mobile_number, email: user?.email, name: user?.name },
    });
    await axiosInstance.post(`${base}/${team.id}/payment-complete`, {
      razorpay_order_id: paid.order_id, razorpay_payment_id: paid.payment_id,
    });
  }, 'Team fee paid');

  const pickCaptain = (registrationId) =>
    act(() => axiosInstance.put(`${base}/${team.id}/captain`, { captain_registration_id: registrationId }), 'Captain updated');

  const removePlayer = (p) => {
    if (!window.confirm(`Remove ${p.player_name} from ${team.team_name}?`)) return;
    act(() => axiosInstance.delete(`${base}/${team.id}/players/${p.registration_id}`), 'Player removed');
  };

  const renameTeam = () => {
    const name = window.prompt('Team name', team.team_name);
    if (!name || name.trim() === team.team_name) return;
    act(() => axiosInstance.patch(`${base}/${team.id}`, { team_name: name.trim() }), 'Team renamed');
  };

  const submit = () => {
    if (!window.confirm('Submit your team for approval? The roster is locked while the admin reviews it.')) return;
    act(() => axiosInstance.post(`${base}/${team.id}/submit`), 'Team submitted for approval');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#3AAFA9] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="team-registration-panel">
      {data?.joined && (
        <div className="bg-[#3AAFA9]/10 rounded-2xl p-4 text-sm" data-testid="joined-team-note">
          <i className="ph-bold ph-users-three mr-1" />
          You're playing for <strong>{data.joined.team_name}</strong> ({data.joined.status}).
        </div>
      )}

      {showCreate ? (
        <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4" data-testid="create-team-form">
          <div>
            <p className="font-extrabold text-[#2B3A67]">Register your team</p>
            <p className="text-xs text-gray-500 mt-1">
              Create the team, then share the invite link with your players. Once the roster is ready, submit it for approval.
            </p>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Team name <span className="text-red-500">*</span></label>
            <input value={teamName} onChange={(e) => setTeamName(e.target.value)} maxLength={100}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#2B3A67]/30"
              placeholder="e.g. Powai Panthers" data-testid="byo-team-name" />
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 mb-2">How will fees be paid?</p>
            <div className="space-y-2">
              {[
                { id: 'team', title: 'I pay one team fee', amount: teamFee, note: 'Players join for free.' },
                { id: 'per_player', title: 'Each player pays their own fee', amount: playerFee, note: 'Paid when they join through your link.' },
              ].map((o) => (
                <button key={o.id} type="button" onClick={() => setFeeMode(o.id)}
                  className={`w-full text-left rounded-xl border-2 p-3 ${feeMode === o.id ? 'border-[#2B3A67] bg-[#2B3A67]/5' : 'border-gray-100'}`}
                  data-testid={`fee-mode-${o.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-bold text-sm">{o.title}</span>
                    <span className="font-bold text-sm text-[#2B3A67]">
                      {o.amount > 0 ? `${rupees(o.amount)}${o.id === 'per_player' ? ' each' : ''}` : 'Free'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{o.note}</p>
                </button>
              ))}
            </div>
          </div>
          <button onClick={createTeam} disabled={busy || !teamName.trim()}
            className="w-full py-3 bg-[#2B3A67] text-white font-extrabold rounded-2xl text-sm disabled:opacity-50"
            data-testid="byo-create-team">Create team</button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl p-5 shadow-sm space-y-4" data-testid="team-dashboard">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-extrabold text-lg text-[#2B3A67] truncate">{team.team_name}</p>
              <span className={`inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded-full ${STATUS[team.status]?.cls}`}
                data-testid="byo-status">{STATUS[team.status]?.label || team.status}</span>
            </div>
            {team.status === 'draft' && (
              <button onClick={renameTeam} className="text-xs font-bold text-[#2B3A67] shrink-0">Rename</button>
            )}
          </div>

          {team.review_notes && (
            <p className="text-sm bg-amber-50 text-amber-800 rounded-xl p-3" data-testid="byo-review-notes">
              <i className="ph-bold ph-chat-circle-text mr-1" /> Admin: {team.review_notes}
            </p>
          )}

          {team.status === 'draft' && (
            <div className="rounded-xl bg-[#3AAFA9]/10 p-3 space-y-2" data-testid="byo-invite">
              <p className="text-sm font-bold text-[#2B3A67]">Invite your players</p>
              <p className="text-xs text-gray-600 break-all bg-white rounded-lg px-2 py-1.5 font-mono">{inviteUrl}</p>
              <div className="flex gap-2">
                <button onClick={copyInvite} className="px-3 py-2 rounded-xl bg-white border border-gray-200 text-xs font-bold"
                  data-testid="byo-copy-invite"><i className="ph-bold ph-copy mr-1" />Copy link</button>
                <a href={`https://wa.me/?text=${encodeURIComponent(`Join ${team.team_name} for ${info?.event_title || 'the tournament'}: ${inviteUrl}`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="px-3 py-2 rounded-xl bg-[#25D366] text-white text-xs font-bold"
                  data-testid="byo-share-whatsapp"><i className="ph-bold ph-whatsapp-logo mr-1" />Share on WhatsApp</a>
              </div>
              <p className="text-[11px] text-gray-500">
                {team.fee_mode === 'per_player'
                  ? `Each player pays ${rupees(team.player_fee_amount)} when joining.`
                  : 'Players join for free — the team fee covers them.'}
              </p>
            </div>
          )}

          <div>
            <p className="text-sm font-bold mb-2">
              Roster <span className="text-gray-400">{team.roster.length} / {team.squad_max}</span>
              <span className="text-xs text-gray-400 font-normal"> (at least {team.squad_min})</span>
            </p>
            {team.roster.length === 0 ? (
              <p className="text-xs text-gray-400">{team.status === 'draft' ? 'No players yet — share the invite link.' : 'No players.'}</p>
            ) : (
              <ul className="space-y-1.5">
                {team.roster.map((p) => (
                  <li key={p.registration_id} className="flex items-center gap-2 text-sm">
                    <span className="font-bold truncate flex-1">{p.player_name}</span>
                    <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-1.5 rounded">{ROLE_SHORT[p.role] || '—'}</span>
                    {p.is_captain ? (
                      <span className="text-[10px] font-bold bg-[#E54562] text-white px-1.5 rounded">CAPTAIN</span>
                    ) : team.status === 'draft' && (
                      <button onClick={() => pickCaptain(p.registration_id)} disabled={busy}
                        className="text-[10px] font-bold border border-gray-200 text-gray-500 px-1.5 rounded"
                        data-testid={`byo-make-captain-${p.registration_id}`}>Make captain</button>
                    )}
                    {team.status === 'draft' && (
                      <button onClick={() => removePlayer(p)} disabled={busy} className="text-xs text-red-500 font-bold"
                        aria-label={`Remove ${p.player_name}`}>×</button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {team.fee_mode === 'team' && team.team_fee_amount > 0 && (
            team.payment_status === 'paid' ? (
              <p className="text-sm text-green-700 font-bold"><i className="ph-bold ph-check-circle mr-1" />Team fee paid</p>
            ) : ['partially_refunded', 'refunded'].includes(team.payment_status) ? (
              <p className="text-sm text-gray-600 font-bold" data-testid="byo-fee-refunded">
                <i className="ph-bold ph-arrow-u-up-left mr-1" />
                Team fee {team.payment_status === 'refunded' ? 'refunded' : 'partly refunded'}
              </p>
            ) : team.status === 'draft' && (
              <button onClick={payTeamFee} disabled={busy}
                className="w-full py-3 bg-[#E54562] text-white font-extrabold rounded-2xl text-sm disabled:opacity-50"
                data-testid="byo-pay-team-fee">Pay team fee {rupees(team.team_fee_amount)}</button>
            )
          )}

          {team.status === 'draft' && (
            <div className="space-y-2">
              {team.submit_blockers.length > 0 && (
                <ul className="text-xs text-gray-500 list-disc pl-5" data-testid="byo-blockers">
                  {team.submit_blockers.map((b) => <li key={b}>{b}</li>)}
                </ul>
              )}
              <button onClick={submit} disabled={busy || team.submit_blockers.length > 0}
                className="w-full py-3.5 bg-[#2B3A67] text-white font-extrabold rounded-2xl text-sm disabled:opacity-50"
                data-testid="byo-submit">Submit team for approval</button>
            </div>
          )}

          {team.status === 'rejected' && (
            <button onClick={() => setStartNew(true)} className="w-full py-3 border-2 border-[#2B3A67] text-[#2B3A67] font-bold rounded-2xl text-sm"
              data-testid="byo-start-new">Register a new team</button>
          )}
        </div>
      )}
    </div>
  );
}
